// src/pages/EngagementReportScreen.jsx
// Relatório de Engajamento premium.
// Rota: /reports/engagement
// Usa EngagementService do projeto.
// Insight Premium: POST /psy/insight/engagement?id=ID_USUARIO
// UI isolada em .eng-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  BellRing,
  CalendarDays,
  CheckCircle2,
  Info,
  LockKeyhole,
  Menu,
  Printer,
  RefreshCw,
  Search,
  Sparkles,
  Users,
  X,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/engagement-report.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  PLAN_VALUE: "valorPlano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  AVISO_ASSINATURA: "avisoAssinatura",
  RECURSOS: "recursos",
};

const DAY_CONFIG = [
  { key: "segunda", legacyKey: "seg", label: "Seg", tone: "blue" },
  { key: "terca", legacyKey: "ter", label: "Ter", tone: "green" },
  { key: "quarta", legacyKey: "qua", label: "Qua", tone: "amber" },
  { key: "quinta", legacyKey: "qui", label: "Qui", tone: "red" },
  { key: "sexta", legacyKey: "sex", label: "Sex", tone: "purple" },
];

const RISK_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "engajados", label: "Engajados" },
  { value: "risco", label: "Em risco" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || json?.detail || json?.error || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function parseJsonStorage(value, fallback = {}) {
  try {
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function isPremiumByPlan(plano) {
  const value = normalizeText(plano);

  return (
    value === "premium" ||
    value === "pro" ||
    value === "psyassist premium" ||
    value.includes("premium")
  );
}

function isStatusLiberado(statusAssinatura) {
  const status = normalizeText(statusAssinatura || "active");
  return ["active", "trialing", "past_due"].includes(status);
}

function isAssinaturaAtiva(value) {
  if (value === "" || value === null || value === undefined) return true;
  return String(value).trim().toLowerCase() !== "false";
}

function getStoredRecursos() {
  return parseJsonStorage(readStorage([AUTH_KEYS.RECURSOS]), {});
}

function canUseFeature(feature) {
  const recursos = getStoredRecursos();

  if (recursos?.[feature] === true) return true;

  if (feature === "ia" && recursos?.insightsIa === true) return true;
  if (feature === "insightsIa" && recursos?.ia === true) return true;
  if (feature === "cobrancas" && recursos?.cobrancas === true) return true;
  if (feature === "relatoriosAvancados" && recursos?.relatoriosAvancados === true) return true;

  return (
    isPremiumByPlan(readStorage([AUTH_KEYS.PLAN])) &&
    isStatusLiberado(readStorage([AUTH_KEYS.STATUS_ASSINATURA])) &&
    isAssinaturaAtiva(readStorage([AUTH_KEYS.ASSINATURA_ATIVA]))
  );
}


function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (String(value).trim() === "") return;
    if (String(value).trim() === "todos") return;
    query.set(key, String(value));
  });
  return query.toString();
}

function getTimeBR(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "P";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function normalizePatientList(list = [], type = "engajados") {
  const rows = Array.isArray(list) ? list : [];

  return rows.map((item, index) => ({
    id: `${type}-${item?._id || item?.id || item?.nome || index}`,
    type,
    nome: String(item?.nome || item?.name || "Paciente").trim(),
    sessoes: safeNumber(item?.sessoes ?? item?.totalSessoes ?? item?.total ?? 0),
    motivo: String(item?.motivo || item?.reason || "").trim(),
  }));
}

function buildSearchText(item) {
  return normalizeText(
    [item.nome, item.type, item.motivo, String(item.sessoes)].filter(Boolean).join(" ")
  );
}

function getEngagementHealth(resumo = {}) {
  const ativos = safeNumber(resumo?.ativos ?? resumo?.percentualPacientesComSessao);
  const sessoes = safeNumber(resumo?.sessoes ?? resumo?.taxaCancelamento);
  const respostas = safeNumber(resumo?.respostas ?? resumo?.pacientesEmRisco);

  const average = (ativos + sessoes + respostas) / 3;

  if (average <= 0) {
    return {
      label: "Sem leitura",
      tone: "neutral",
      icon: "i",
      description: "Ainda não há dados suficientes para avaliar o engajamento.",
      score: 0,
    };
  }

  if (average >= 75) {
    return {
      label: "Engajamento forte",
      tone: "good",
      icon: "good",
      description: "Os indicadores mostram boa aderência dos pacientes.",
      score: average,
    };
  }

  if (average >= 45) {
    return {
      label: "Acompanhar",
      tone: "warning",
      icon: "!",
      description: "O engajamento está razoável, mas precisa de acompanhamento.",
      score: average,
    };
  }

  return {
    label: "Atenção",
    tone: "bad",
    icon: "!",
    description: "A base apresenta sinais de baixa aderência.",
    score: average,
  };
}

function buildEngagementDetails(data = {}) {
  const resumo = data?.resumo || {};
  const semana = data?.semana || {};
  const engajados = normalizePatientList(data?.maisEngajados, "engajados");
  const risco = normalizePatientList(data?.emRisco, "risco");

  const weekly = DAY_CONFIG.map((day) => `${day.label}: ${safeNumber(semana?.[day.key] ?? semana?.[day.legacyKey])}`).join(", ");

  const topEngaged = engajados
    .slice(0, 6)
    .map((patient) => `${patient.nome} (${patient.sessoes} sessão(ões))`)
    .join("; ");

  const topRisk = risco
    .slice(0, 6)
    .map((patient) => `${patient.nome}${patient.motivo ? ` - ${patient.motivo}` : ""}`)
    .join("; ");

  return [
    `Pacientes com sessão: ${safeNumber(resumo?.percentualPacientesComSessao ?? resumo?.ativos)}%`,
    `Sessões no período: ${safeNumber(resumo?.totalSessoes ?? resumo?.sessoes)}`,
    `Pacientes em risco: ${safeNumber(resumo?.pacientesEmRisco ?? resumo?.respostas)}`,
    `Engajamento semanal: ${weekly || "sem dados"}`,
    `Pacientes mais engajados: ${topEngaged || "sem dados"}`,
    `Pacientes em risco: ${topRisk || "sem dados"}`,
  ].join("\n");
}

function splitInsightText(text = "") {
  const clean = String(text || "").trim();

  if (!clean) return [];

  const lines = clean
    .split(/\n+/)
    .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
    .filter(Boolean);

  return lines.length ? lines : [clean];
}

function HealthStatusIcon({ tone }) {
  const Icon =
    tone === "good"
      ? CheckCircle2
      : tone === "bad"
        ? AlertCircle
        : tone === "warning"
          ? AlertTriangle
          : Info;

  return <Icon size={22} aria-hidden="true" />;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  description,
  tone = "blue",
}) {
  return (
    <article className={`eng-kpi eng-kpi--${tone}`}>
      <div className="eng-kpi__icon" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function WeeklyBars({ days, values }) {
  const safeValues = days.map((day) => safeNumber(values?.[day.key] ?? values?.[day.legacyKey]));
  const max = Math.max(1, ...safeValues);

  return (
    <div className="eng-bars" role="img" aria-label="Engajamento semanal">
      <div className="eng-bars__y" aria-hidden="true">
        <span>{max}</span>
        <span>{Math.round(max / 2)}</span>
        <span>0</span>
      </div>

      <div className="eng-bars__plot">
        {days.map((day) => {
          const value = safeNumber(values?.[day.key] ?? values?.[day.legacyKey]);
          const height = Math.max(8, Math.round((value / max) * 138));

          return (
            <div className="eng-bars__col" key={day.key} title={`${day.label}: ${value}`}>
              <div className="eng-bars__bar-wrap">
                <span
                  className={`eng-bars__bar tone-${day.tone}`}
                  style={{ height: `${height}px` }}
                />
              </div>

              <span className="eng-bars__label">{day.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PatientRow({ patient }) {
  const isRisk = patient.type === "risco";

  return (
    <article className={`eng-row ${isRisk ? "eng-row--risk" : "eng-row--good"}`}>
      <div className="eng-row__avatar" aria-hidden="true">
        {isRisk ? "!" : getInitials(patient.nome)}
      </div>

      <div className="eng-row__content">
        <strong>{patient.nome}</strong>
        <span>
          {isRisk
            ? patient.motivo || "Possível risco de abandono."
            : `Engajamento: ${patient.sessoes} sessão(ões)`}
        </span>
      </div>

      <div className={`eng-row__tag ${isRisk ? "is-risk" : "is-good"}`}>
        {isRisk ? "Em risco" : "Engajado"}
      </div>
    </article>
  );
}

function EngagementSkeleton() {
  return (
    <div className="eng-skeleton" aria-hidden="true">
      <div className="eng-skeleton__hero" />
      <div className="eng-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="eng-skeleton__grid">
        <span />
        <span />
      </div>
      <div className="eng-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  message,
  onRetry,
  onBack,
  isError = false,
}) {
  return (
    <section
      className={`eng-state ${isError ? "eng-state--error" : ""}`}
    >
      <div className="eng-state__icon" aria-hidden="true">
        {isError ? (
          <AlertCircle size={24} />
        ) : (
          <Activity size={24} />
        )}
      </div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="eng-state__actions">
        <button
          className="eng-btn eng-btn--ghost-dark"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Indicadores
        </button>

        <button
          className="eng-btn eng-btn--primary"
          type="button"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function EngagementReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [reportData, setReportData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPremium, setIsPremium] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todos");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [diasRisco, setDiasRisco] = useState(30);

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!token || !userId) {
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate]);

  const fetchInsightsFromAI = useCallback(
    async (data) => {
      const auth = authOrRedirect();

      if (!auth || !data) return;

      setInsightLoading(true);
      setInsightError("");

      try {
        const engagementDetails = buildEngagementDetails(data);

        const response = await fetch(
          `${API_BASE}/psy/insight/engagement?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: auth.userId,
              engagementDetails,
            }),
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401) {
          navigate("/login", { replace: true });
          return;
        }

        if (response.status === 403) {
          const message = await readErrorMessage(response);
          setInsightError(message || "Recurso disponível apenas para o plano Premium.");
          setInsights([]);
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          setInsightError(message || "Não foi possível gerar o insight de engajamento.");
          setInsights([]);
          return;
        }

        const json = await response.json();
        const text = json?.insight ? String(json.insight).trim() : "";

        setInsights(splitInsightText(text));
      } catch (error) {
        console.error("Erro ao gerar insight de engajamento:", error);
        setInsightError("Falha de comunicação ao gerar o insight de engajamento.");
        setInsights([]);
      } finally {
        setInsightLoading(false);
      }
    },
    [authOrRedirect, navigate]
  );

  const loadReport = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setErrorMessage("");

      try {
        const premium = canUseFeature("ia") || canUseFeature("insightsIa") || canUseFeature("relatoriosAvancados");

        setIsPremium(premium);

        const auth = authOrRedirect();
        if (!auth) return;

        const params = buildQuery({
          id: auth.userId,
          dataInicio,
          dataFim,
          diasRisco,
        });

        const response = await fetch(`${API_BASE}/psy/report/engagement?${params}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        });

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          navigate("/login", { replace: true });
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          throw new Error(message || `Erro ao carregar relatório (${response.status}).`);
        }

        const data = await response.json().catch(() => null);

        if (!data) {
          setReportData(null);
          setInsights([]);
          setErrorMessage("Erro ao carregar os dados do relatório.");
          return;
        }

        setReportData(data);
        setLastUpdatedAt(new Date());

        setInsights([]);
        setInsightError("");
      } catch (error) {
        console.error("Erro ao carregar relatório de engajamento:", error);
        setReportData(null);
        setInsights([]);
        setErrorMessage(`Erro inesperado: ${error?.message || String(error)}`);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, dataFim, dataInicio, diasRisco, navigate]
  );

  const handleGenerateInsight = useCallback(async () => {
    if (!reportData) return;

    await fetchInsightsFromAI(reportData);
  }, [fetchInsightsFromAI, reportData]);

  useEffect(() => {
    let mounted = true;

    async function run() {
      if (!mounted) return;
      await loadReport();
    }

    run();

    return () => {
      mounted = false;
    };
  }, [loadReport]);

  const resumo = useMemo(
    () =>
      reportData?.resumo && typeof reportData.resumo === "object"
        ? reportData.resumo
        : {},
    [reportData]
  );
  const semana = useMemo(
    () => (reportData?.semana && typeof reportData.semana === "object" ? reportData.semana : {}),
    [reportData]
  );

  const maisEngajados = useMemo(
    () => normalizePatientList(reportData?.maisEngajados, "engajados"),
    [reportData]
  );

  const emRisco = useMemo(
    () => normalizePatientList(reportData?.emRisco, "risco"),
    [reportData]
  );

  const combinedPatients = useMemo(
    () => [...maisEngajados, ...emRisco],
    [maisEngajados, emRisco]
  );

  const filteredPatients = useMemo(() => {
    const query = normalizeText(search);

    return combinedPatients.filter((patient) => {
      const matchesFilter =
        filter === "todos" ||
        (filter === "engajados" && patient.type === "engajados") ||
        (filter === "risco" && patient.type === "risco");

      const matchesSearch = !query || buildSearchText(patient).includes(query);

      return matchesFilter && matchesSearch;
    });
  }, [combinedPatients, filter, search]);

  const health = useMemo(() => getEngagementHealth(resumo), [resumo]);

  const totalWeekly = useMemo(() => {
    return DAY_CONFIG.reduce((sum, day) => sum + safeNumber(semana?.[day.key] ?? semana?.[day.legacyKey]), 0);
  }, [semana]);

  const reload = () => loadReport({ silent: true });
  const clearManagementFilters = () => {
    setDataInicio("");
    setDataFim("");
    setDiasRisco(30);
    setSearch("");
    setFilter("todos");
  };
  const goBack = () => navigate("/reports");
  const goPatients = () => navigate("/clients");
  const printReport = () => window.print();

  return (
    <div className="eng-page">
      <div className="eng-bg" aria-hidden="true">
        <span className="eng-bg__orb eng-bg__orb--one" />
        <span className="eng-bg__orb eng-bg__orb--two" />
        <span className="eng-bg__orb eng-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="eng-topbar">
        <button
          className="eng-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="eng-brand">
          <img src={Logo} alt="PsyAssist" className="eng-brand__logo" />
          <div className="eng-brand__text">
            <strong>Relatório de Engajamento</strong>
            <span>Resumo, semanal, risco e IA • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="eng-top-actions">
          <button
            className="eng-btn eng-btn--ghost eng-btn--desktop"
            type="button"
            onClick={goBack}
          >
            <ArrowLeft size={16} />
            Indicadores
          </button>

          <button
            className="eng-btn eng-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(errorMessage) || !reportData}
          >
            <Printer size={16} />
            Imprimir
          </button>

          <button
            className="eng-btn eng-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "report-spin" : ""}
              aria-hidden="true"
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="eng-main">
        {loading ? (
          <EngagementSkeleton />
        ) : errorMessage ? (
          <EmptyState
            title="Não foi possível carregar o engajamento."
            message={errorMessage}
            onRetry={reload}
            onBack={goBack}
            isError
          />
        ) : !reportData ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há dados suficientes para montar este relatório."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="eng-hero">
              <div>
                <span className="eng-kicker">
                  <Activity size={14} />
                  Análise comportamental
                </span>
                <h1>Engajamento</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe pacientes ativos, sessões, respostas, risco de abandono
                  e insights para melhorar aderência clínica.
                </p>
              </div>

              <aside className={`eng-hero-card eng-hero-card--${health.tone}`}>
                <div className="eng-hero-card__icon" aria-hidden="true">
                  <HealthStatusIcon tone={health.tone} />
                </div>

                <div>
                  <small>Status do engajamento</small>
                  <strong>{health.label}</strong>
                  <span>
                    Score médio: {percentBR(health.score)}% • {health.description}
                  </span>
                </div>
              </aside>
            </section>

            <section className="eng-panel eng-management-filters">
              <div className="eng-panel__head">
                <div>
                  <span>Filtros de gestão</span>
                  <h2>Refine o engajamento</h2>
                  <p>Analise um período específico e ajuste a régua de risco operacional.</p>
                </div>
              </div>

              <div className="eng-filter-grid">
                <label className="eng-filter-field">
                  <span>Data início</span>
                  <input type="date" value={dataInicio} onChange={(event) => setDataInicio(event.target.value)} />
                </label>
                <label className="eng-filter-field">
                  <span>Data fim</span>
                  <input type="date" value={dataFim} onChange={(event) => setDataFim(event.target.value)} />
                </label>
                <label className="eng-filter-field">
                  <span>Dias de risco</span>
                  <input type="number" min="1" max="365" value={diasRisco} onChange={(event) => setDiasRisco(event.target.value)} />
                </label>
                <div className="eng-filter-actions">
                  <button className="eng-btn eng-btn--ghost-dark" type="button" onClick={clearManagementFilters}>Limpar</button>
                  <button className="eng-btn eng-btn--primary" type="button" onClick={reload} disabled={refreshing}>Aplicar</button>
                </div>
              </div>
            </section>

            <section className="eng-kpis">
              <KpiCard
                icon={Users}
                label="Ativos"
                value={`${safeNumber(resumo?.percentualPacientesComSessao ?? resumo?.ativos)}%`}
                description="Pacientes com atividade no período"
                tone="blue"
              />

              <KpiCard
                icon={CalendarDays}
                label="Sessões"
                value={safeNumber(resumo?.totalSessoes ?? resumo?.sessoes)}
                description="Aderência aos atendimentos"
                tone="green"
              />

              <KpiCard
                icon={BellRing}
                label="Em risco"
                value={safeNumber(resumo?.pacientesEmRisco ?? resumo?.respostas)}
                description="Interações e retornos"
                tone="amber"
              />
            </section>

            <section className="eng-grid2">
              <div className="eng-panel">
                <div className="eng-panel__head">
                  <div>
                    <span>Semanal</span>
                    <h2>Engajamento semanal</h2>
                    <p>Volume distribuído de segunda a sexta.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="eng-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <WeeklyBars days={DAY_CONFIG} values={semana} />
              </div>

              <div className="eng-panel">
                <div className="eng-panel__head">
                  <div>
                    <span>Resumo</span>
                    <h2>Leitura operacional</h2>
                    <p>Principais sinais de aderência e risco.</p>
                  </div>
                </div>

                <div className="eng-score-card">
                  <span>Movimento semanal</span>
                  <strong>{totalWeekly}</strong>
                  <p>
                    {maisEngajados.length} paciente(s) com bom engajamento e{" "}
                    {emRisco.length} paciente(s) em risco operacional.
                  </p>

                  <button className="eng-btn eng-btn--ghost-dark" type="button" onClick={goPatients}>
                    Abrir pacientes
                  </button>
                </div>
              </div>
            </section>

            <section className="eng-panel">
              <div className="eng-panel__head eng-panel__head--with-tools">
                <div>
                  <span>Pacientes</span>
                  <h2>Engajamento e risco</h2>
                  <p>Busque pacientes engajados ou com risco de abandono.</p>
                </div>

                <div className="eng-counter">
                  {filteredPatients.length} de {combinedPatients.length}
                </div>
              </div>

              <div className="eng-toolbar">
                <label className="eng-search">
                  <span aria-hidden="true"><Search size={16} /></span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar paciente..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      <X size={14} />
                    </button>
                  ) : null}
                </label>

                <div className="eng-filter-group" aria-label="Filtro de pacientes">
                  {RISK_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={filter === option.value ? "is-active" : ""}
                      onClick={() => setFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredPatients.length === 0 ? (
                <div className="eng-empty">Nenhum paciente encontrado com os filtros atuais.</div>
              ) : (
                <div className="eng-list">
                  {filteredPatients.map((patient) => (
                    <PatientRow key={patient.id} patient={patient} />
                  ))}
                </div>
              )}
            </section>

            <section className="eng-panel">
              <div className="eng-panel__head eng-panel__head--ai">
                <div>
                  <span>IA Premium</span>
                  <h2>
                    Insights com IA
                    {isPremium ? <b>Premium</b> : null}
                  </h2>
                  <p>Análise automática de engajamento para apoiar decisões.</p>
                </div>

                {isPremium ? (
                  <button
                    className="eng-ai-btn"
                    type="button"
                    onClick={handleGenerateInsight}
                    disabled={insightLoading || refreshing}
                  >
                    {insightLoading ? "Gerando..." : "Gerar insight"}
                  </button>
                ) : null}
              </div>

              <div className="eng-insight">
                {insightError ? (
                  <div className="eng-insight__error">{insightError}</div>
                ) : null}

                {!isPremium ? (
                  <div className="eng-insight__premium-lock">
                    <div><LockKeyhole size={24} /></div>
                    <strong>Recurso Premium</strong>
                    <span>Atualize seu plano para gerar insights de engajamento com IA.</span>

                    <button
                      className="eng-btn eng-btn--primary"
                      type="button"
                      onClick={() => navigate("/plan-management")}
                    >
                      Ver planos
                    </button>
                  </div>
                ) : insightLoading ? (
                  <div className="eng-insight__loading">
                    <span className="eng-spinner" />
                    Gerando insight de engajamento...
                  </div>
                ) : insights.length ? (
                  <ul className="eng-insight__list">
                    {insights.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="eng-insight__ready">
                    <div><Sparkles size={24} /></div>
                    <strong>Insight pronto para gerar</strong>
                    <span>
                      Clique em “Gerar insight” para analisar pacientes ativos,
                      aderência, respostas e risco de abandono.
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section className="eng-bottom-actions">
              <button className="eng-btn eng-btn--ghost-dark" type="button" onClick={goBack}>
                <ArrowLeft size={16} />
                Central de indicadores
              </button>

              <button className="eng-btn eng-btn--primary" type="button" onClick={goPatients}>
                <Users size={16} />
                Abrir pacientes
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
